import { useState, useEffect } from 'react'
import { FiDownload, FiCalendar, FiMapPin, FiClock } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { getMyTickets, downloadTicket } from '../services/api'

export default function MyTicketsPage() {
    const [tickets, setTickets] = useState([])
    const [loading, setLoading] = useState(true)
    const [filter, setFilter] = useState('upcoming') // upcoming, past, all
    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)

    const currentLanguage = localStorage.getItem('app_lang')

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo(0, 0)
        }, 0);
    }, [])

    useEffect(() => {
        window.scrollTo(0, 0)
        loadTickets()
    }, [page])

    const loadTickets = async () => {
        try {
            const res = await getMyTickets({ page: page })

            const data = res.data

            setTickets(data.data)
            setLastPage(data.last_page)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    const handleDownload = async (ticket) => {
        try {
            const res = await downloadTicket(ticket.id)
            const blob = new Blob([res.data], { type: 'application/pdf' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.style.display = 'none'
            a.href = url
            a.download = `ticket-${ticket.uuid}.pdf`
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        }
    }

    const filteredTickets = tickets.filter(ticket => {
        const performanceDate = new Date(ticket.performance?.start_time)
        const now = new Date()

        if (filter === 'upcoming') return performanceDate > now
        if (filter === 'past') return performanceDate <= now
        return true
    })

    const getStatusBadge = (status) => {
        switch (status) {
            case 'valid':
                return <span className="px-2 pt-[3px] pb-[5px] rounded-full text-xs bg-green-500/20 text-green-800">{window.t('validen')}</span>
            case 'used':
                return <span className="px-2 pt-[3px] pb-[5px] rounded-full text-xs bg-gray-500/20 text-gray-800">{window.t('izpolzvan')}</span>
            case 'cancelled':
                return <span className="px-2 pt-[3px] pb-[5px] rounded-full text-xs bg-red-500/20 text-red-800">{window.t('anuliran')}</span>
            default:
                return null
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen pt-16 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        )
    }

    return (
        <div className="min-h-screen pt-16 pb-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <h1 className="text-3xl font-bold text-white mb-8">{window.t('moite_bileti')}</h1>

                {/* Filter Tabs */}
                <div className="flex gap-2 mb-8">
                    {[
                        { value: 'upcoming', label: `${window.t('predstoyashti')}` },
                        { value: 'past', label: `${window.t('minali')}` },
                        { value: 'all', label: `${window.t('vsichki')}` },
                    ].map(tab => (
                        <button
                            key={tab.value}
                            onClick={() => setFilter(tab.value)}
                            className={`px-4 pt-[0.4rem] pb-[0.5rem] rounded-full text-sm font-medium transition-all whitespace-nowrap ${filter === tab.value
                                ? 'bg-primary-500 text-gray-900'
                                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {filteredTickets.length === 0 ? (
                    <div className="card p-12 text-center">
                        <p className="text-gray-400 text-lg mb-4">{window.t('nyama_namereni_bileti')}</p>
                        <a href="/" className="btn-primary">{window.t('razgledayte_sabitiyata')}</a>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredTickets.map(ticket => (
                            <div key={ticket.id} className="card p-6 hover:border-primary-500/50 transition-colors">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="text-lg font-bold text-white">
                                                {ticket.performance?.event?.title}
                                            </h3>
                                            {getStatusBadge(ticket.status)}
                                        </div>

                                        <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                                            <div className="flex items-center gap-1">
                                                <FiCalendar />
                                                {currentLanguage === 'en'
                                                    ? new Date(ticket.performance?.start_time).toLocaleDateString('en-EN')
                                                    : new Date(ticket.performance?.start_time).toLocaleDateString('bg-BG')
                                                }
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <FiClock />
                                                {currentLanguage === 'en'
                                                    ? new Date(ticket.performance?.start_time).toLocaleTimeString('en-US', {
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: true
                                                    })
                                                    : new Date(ticket.performance?.start_time).toLocaleTimeString('bg-BG', {
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: false
                                                    })
                                                }
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <FiMapPin />
                                                {ticket.performance?.event?.hall?.name}
                                            </div>
                                        </div>

                                        <div className="mt-3 flex flex-wrap gap-3 text-sm">
                                            <span className="px-3 py-1 rounded-full bg-gray-800 text-gray-300">
                                                {window.t('myasto')} {ticket.seat?.row} / {ticket.seat?.number}
                                            </span>
                                            <span className="px-3 py-1 rounded-full bg-gray-800 text-gray-300">
                                                {currentLanguage === 'en' ? (ticket.ticket_type?.name_en || ticket.ticket_type?.name) : ticket.ticket_type?.name}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <div className="text-gray-400 text-sm">{window.t('tsena')}</div>
                                            <div className="text-xl font-bold text-white">€{ticket.price}</div>
                                        </div>

                                        {ticket.status === 'valid' && (
                                            <button
                                                onClick={() => handleDownload(ticket)}
                                                className="btn-outline px-4 py-2"
                                            >
                                                <FiDownload className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {ticket.verification_terms && (
                                    <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg text-yellow-600 text-sm">
                                        ⚠️ {currentLanguage === 'en' ? (ticket.ticket_type?.verification_terms_en || ticket.verification_terms) : ticket.verification_terms}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="mt-8 text-center">
                <select
                    value={page}
                    onChange={(e) => setPage(Number(e.target.value))}
                    className="bg-gray-800 text-gray-300 border border-gray-700 rounded-lg px-3 py-2 text-sm outline-none cursor-pointer hover:text-white"
                >
                    {Array.from({ length: lastPage }, (_, i) => i + 1).map((p) => (
                        <option key={p} value={p}>
                            {p}
                        </option>
                    ))}
                </select>
            </div>

        </div>
    )
}
